/* eslint-disable jira/filename-case */

import {
	type AnchorHTMLAttributes,
	type MouseEvent,
	type KeyboardEvent,
	type FocusEvent,
	type ReactNode,
	createElement,
	forwardRef,
	useCallback,
	useEffect,
	useState,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';
import { createPath } from 'history';
import type { MatchParams, Query, Route } from './common/types.tsx';
import { generateLocationFromPath } from './common/utils/generate-location/index.tsx';
import { createRouterContext } from './common/utils/router-context/index.tsx';
import { useRouterStoreActions } from './controllers/router-store/index.tsx';
import { useTimeout } from './controllers/use-timeout/index.tsx';
import { handleNavigation } from './handleNavigation.tsx';

const PREFETCH_DELAY = 225;

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
	children: ReactNode;
	target?: '_blank' | '_self' | '_parent' | '_top';
	href?: string;
	to?: string | Route | Promise<{ default: Route } | Route>;
	replace?: boolean;
	type?: 'a' | 'button';
	onClick?: (e: MouseEvent | KeyboardEvent) => void;
	onMouseEnter?: (e: MouseEvent) => void;
	onMouseLeave?: (e: MouseEvent) => void;
	onPointerDown?: (e: PointerEvent) => void;
	params?: MatchParams;
	query?: Query;
	prefetch?: false | 'hover' | 'mount';
	state?: unknown;
};

export const Link: ForwardRefExoticComponent<
	AnchorHTMLAttributes<HTMLAnchorElement> & {
		children: ReactNode;
		target?: '_blank' | '_self' | '_parent' | '_top';
		href?: string;
		to?: string | Route | Promise<{ default: Route } | Route>;
		replace?: boolean;
		type?: 'a' | 'button';
		onClick?: (e: MouseEvent | KeyboardEvent) => void;
		onMouseEnter?: (e: MouseEvent) => void;
		onMouseLeave?: (e: MouseEvent) => void;
		onPointerDown?: (e: PointerEvent) => void;
		params?: MatchParams;
		query?: Query;
		prefetch?: false | 'hover' | 'mount';
		state?: unknown;
	} & RefAttributes<HTMLAnchorElement | HTMLButtonElement>
> = forwardRef<HTMLButtonElement | HTMLAnchorElement, LinkProps>(
	(
		{
			children,
			target = '_self',
			replace = false,
			href = undefined,
			to = undefined,
			onClick = undefined,
			onMouseEnter = undefined,
			onMouseLeave = undefined,
			onPointerDown = undefined,
			onFocus = undefined,
			onBlur = undefined,
			type: linkType = 'a',
			params,
			query,
			prefetch = false,
			state = undefined,
			...rest
		},
		ref,
	) => {
		const routerActions = useRouterStoreActions();

		const { schedule, cancel } = useTimeout(PREFETCH_DELAY);

		const validLinkType = linkType === 'a' || linkType === 'button' ? linkType : 'a';

		// oxlint-disable-next-line react/react-compiler
		const [route, setRoute] = useState<Route | undefined>(() => {
			if (to && typeof to !== 'string') {
				// oxlint-disable-next-line react/react-compiler
				if ('then' in to) to.then((r) => setRoute('default' in r ? r.default : r));
				else return to;
			}
		});

		useEffect(() => {
			if (to && typeof to !== 'string' && !route && 'then' in to) {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
				to.then((r) => setRoute('default' in r ? r.default : r));
			}
		}, [to, route]);

		let routeValue: Route | undefined = route;
		if (!routeValue && to && typeof to !== 'string' && !('then' in to)) {
			// use the prop instead of the value in useState
			routeValue = to;
		}

		const routeAttributes = {
			params,
			query,
			basePath: routerActions.getBasePath(),
		};
		const linkDestination =
			// oxlint-disable-next-line no-nested-ternary
			href != null
				? href
				: typeof to !== 'string'
					? (routeValue &&
							createPath(generateLocationFromPath(routeValue.path, routeAttributes))) ||
						''
					: to;
		const staticBasePath = href == null && typeof to === 'string' ? routeAttributes.basePath : '';

		const triggerPrefetch = useCallback(() => {
			// ignore if async route not ready yet
			// oxlint-disable-next-line react/react-compiler
			if (typeof to !== 'string' && !routeValue) return;

			const context =
				typeof to !== 'string' && routeValue
					? // oxlint-disable-next-line react/react-compiler
						createRouterContext(routeValue, { params, query })
					: null;
			routerActions.prefetchRoute(linkDestination, context);
			// omit params & query as already in linkDestination

			// oxlint-disable-next-line react-hooks/exhaustive-deps
		}, [routeValue, linkDestination, routerActions]);

		useEffect(() => {
			if (prefetch === 'mount') {
				schedule(triggerPrefetch);
			}

			return cancel;
		}, [prefetch, schedule, cancel, triggerPrefetch]);

		const handleLinkPress = (e: MouseEvent | KeyboardEvent) =>
			handleNavigation(e, {
				onClick,
				target,
				replace,
				routerActions,
				href: linkDestination,
				to: routeValue && [routeValue, { params, query }],
				state,
			});

		const handleMouseEnter = (e: MouseEvent) => {
			if (prefetch === 'hover') {
				schedule(triggerPrefetch);
			}
			onMouseEnter && onMouseEnter(e);
		};

		const handleMouseLeave = (e: MouseEvent) => {
			if (prefetch === 'hover') {
				cancel();
			}
			onMouseLeave && onMouseLeave(e);
		};

		const handleFocus = (e: FocusEvent<HTMLAnchorElement>) => {
			if (prefetch === 'hover') {
				schedule(triggerPrefetch);
			}
			onFocus && onFocus(e);
		};

		const handleBlur = (e: FocusEvent<HTMLAnchorElement>) => {
			if (prefetch === 'hover') {
				cancel();
			}
			onBlur && onBlur(e);
		};

		const handlePointerDown = (e: PointerEvent) => {
			if (prefetch === 'hover') {
				cancel();
				triggerPrefetch();
			}
			onPointerDown && onPointerDown(e);
		};

		return createElement(
			validLinkType,
			// oxlint-disable-next-line react/react-compiler
			{
				...rest,
				href: `${staticBasePath}${linkDestination}`,
				target,
				onClick: handleLinkPress,
				onKeyDown: handleLinkPress,
				onMouseEnter: handleMouseEnter,
				onMouseLeave: handleMouseLeave,
				onFocus: handleFocus,
				onBlur: handleBlur,
				onPointerDown: handlePointerDown,
				ref,
			},
			children,
		);
	},
);

Link.displayName = 'Link';
